import React, { useState } from "react";
import styled from "@emotion/styled";
import ReactHtmlParser from "@mediaspace/shared/ui/html-react-parser";
import RegistrationFormContainer from "./RegistrationFormContainer";
import SiteRegistrationProps, { RegistrationType } from "./SiteRegistrationProps";
import RegistrationComplete from "./RegistrationComplete";
import { Toast, Typography } from "@kaltura/ds-react-components";
import { systemWidth } from "@mediaspace/shared/styled";
import { Snackbar } from "@mui/material";
import { translate } from "@mediaspace/shared/utils";
import { ContentContext } from "./ContentContext";

const RegistrationContainer = styled("div", {
    shouldForwardProp(propName: PropertyKey): boolean {
        return propName !== "fullWidth";
    }
})<{ fullWidth?: boolean }>(({ theme, fullWidth }) => ({
    "&": fullWidth && systemWidth({ theme }),
    paddingBottom: theme.spacing(7.5),
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
        paddingBottom: theme.spacing(5),
    },
}));

const H1 = styled(Typography)(({ theme, variant }) => ({
    display: "block",
    marginTop: theme.spacing(7.5),
    marginBottom: theme.spacing(4),
    ...(variant === "h2" && {
        marginTop: 0,
        marginBottom: theme.spacing(3),
        textAlign: "center",
    }),
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
        marginTop: theme.spacing(3),
    },
}));

const TitleText = styled(Typography, {
    shouldForwardProp(propName: PropertyKey): boolean {
        return propName !== "fullWidth";
    }
})<{ fullWidth?: boolean }>(({ theme, fullWidth }) => ({
    marginBottom: fullWidth ? theme.spacing(7.5) : theme.spacing(1),
    display: "block",
    [theme.breakpoints.up(theme.breakpoints.values.md)]: {
        ...(fullWidth && {
            width: "70%",
        }),
    },
    [theme.breakpoints.up(theme.breakpoints.values.lg)]: {
        ...(fullWidth && {
            width: "60%",
        }),
    },
}));

/**
 * Site Registration Main Component
 */
export function SiteRegistration({
    fullWidth = true,
    title,
    userData,
    sections,
    registrationType = RegistrationType.NEW,
    titleText,
    recaptchaSiteKey,
    recaptchaTheme,
    recapthcaMode,
    fieldsMappingToMarketo,
    marketoResponseTimeout,
    action,
    completeTitle,
    completeDescriptionHtml,
    completeButtonText,
    completeButtonUrl,
    onCompleteScriptContent,
    validateSsoDomains = false,
    registerOtherUser = false,
    onRegistrationCompleted,
}: SiteRegistrationProps) {
    const [registered, setRegistered] = useState(false);
    const [showNotification, setShowNotification] = useState<boolean>(registerOtherUser);

    const handleRegister = (redirectUrl?: string) => {
        if(redirectUrl) {
            window.location.href = redirectUrl;
        } else {
            setRegistered(true);
        }

        if (onRegistrationCompleted) {
            onRegistrationCompleted();
        }
    };

    return (
        <ContentContext.Provider value={{ fullWidth: fullWidth }}>
            <RegistrationContainer className={"kms-ds-site-registration"} fullWidth={fullWidth}>
                {registered && (
                    <RegistrationComplete
                        registrationType={registrationType}
                        completeTitle={completeTitle}
                        completeDescriptionHtml={completeDescriptionHtml}
                        completeButtonText={completeButtonText}
                        completeButtonUrl={completeButtonUrl}
                        onCompleteScriptContent={onCompleteScriptContent}
                    />
                )}
                {!registered && (
                    <>
                        <H1 variant={fullWidth ? "h1" : "h2"} component={"h1"} className={"kms-ds-site-registration-title"}>
                            {title}
                        </H1>
                        <TitleText
                            fullWidth={fullWidth}
                            variant={"body1"}
                            className={"kms-ds-site-registration--title-text"}
                        >
                            {ReactHtmlParser(titleText)}
                        </TitleText>
                        <RegistrationFormContainer
                            userData={userData}
                            sections={sections}
                            recaptchaSiteKey={recaptchaSiteKey}
                            recaptchaTheme={recaptchaTheme}
                            recapthcaMode={recapthcaMode}
                            action={action}
                            registrationType={registrationType}
                            onRegister={handleRegister}
                            fieldsMappingToMarketo={fieldsMappingToMarketo}
                            marketoResponseTimeout={marketoResponseTimeout}
                            validateSsoDomains={validateSsoDomains}
                            registerOtherUser={registerOtherUser}
                        />
                    </>
                )}
                {registerOtherUser &&
                    <Snackbar open={showNotification} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                        <Toast
                            onClose={() => setShowNotification(false)}
                            severity={"info"}
                        >
                            <span>{translate("Please note that you are already logged in and there is no need to register")}</span>
                        </Toast>
                    </Snackbar>
                }
            </RegistrationContainer>
        </ContentContext.Provider>
    );
}

export default SiteRegistration;
